import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel
} from "./ui/dropdown-menu";
import { Search, Map, Users, LogOut, LayoutDashboard, User as UserIcon } from "lucide-react";
import LocationDropdown from "./LocationDropdown";
import Logo from "./Logo";

const Header = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogin = () => {
    navigate("/login");
  };

  const isActive = (p) => location.pathname === p;

  return (
    <header className="glass-header border-b border-border/40 sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <Logo size={40} className="rounded-lg" />
          <div className="font-heading">
            <div className="text-lg font-black leading-none tracking-tight">Kulintara</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Cita rasa Indonesia</div>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-2">
          <LocationDropdown />
          <nav className="flex items-center gap-1">
            {[
              { to: "/restaurants", label: "Restoran", icon: Search, tid: "nav-restaurants" },
              { to: "/influencers", label: "Influencer", icon: Users, tid: "nav-influencers" },
              { to: "/map", label: "Peta", icon: Map, tid: "nav-map" },
            ].map(({ to, label, icon: Icon, tid }) => (
              <Link
                key={to}
                to={to}
                data-testid={tid}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive(to) ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex md:hidden items-center gap-2">
          <LocationDropdown />
        </div>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full hover:bg-secondary/60 p-1 pr-3 transition-colors" data-testid="user-menu-trigger">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback>{user.name?.[0] || "U"}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-semibold">{user.name}</span>
                    <span className="text-xs text-muted-foreground truncate">{user.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/dashboard/profile")} data-testid="menu-profile">
                  <UserIcon className="w-4 h-4 mr-2" /> Profil Saya
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button onClick={handleLogin} className="bg-primary hover:bg-primary/90 rounded-full" data-testid="login-btn">
              Masuk
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
